import { useEffect, useState } from "react";

/**
 * The revision info attached to a file version. SERVER SCHEMA GAP: `directoryDataFileItemSchema`
 * declares `revision` as `{ value?: string }` only, but this panel also reads `createdBy` and
 * `createdDate` off it — and `files.versions[].revision` is declared a plain `string` elsewhere
 * (fileOperations.schemas.ts:708). Three declarations, three shapes; this is what the panel reads.
 */
type RevisionInfo = { value?: string; createdBy?: string; createdDate?: number };

/**
 * A file version from the `fileVersions` store slice, plus the two members this panel reads that the
 * slice's own element type does not declare (`revision`, `fileVersionNumber`) — a third place where
 * the revision shape is under-declared.
 */
type FileVersionLike = NonNullable<RootState["fileVersions"][string]>[number] & {
  revision?: RevisionInfo;
  fileVersionNumber?: number;
};

/** One rendered row: the spread file version, with its revision guaranteed present by the filter. */
type RevisionRow = FileVersionLike;
import { useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { useTranslation } from "@i18n";
import { RootState } from "@Reducers/index";

function RevisionContent({ resourceId }: { resourceId?: string }) {
  const { t } = useTranslation();
  const fileVersions = useSelector((state: RootState) => state.fileVersions[resourceId!]);
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);

  useEffect(() => {
    if (fileVersions && fileVersions.length) {
      const allFileVersionRevisions = fileVersions
        .filter((fileVersion: FileVersionLike) => fileVersion.revision)
        .map((fileVersion: FileVersionLike) => ({
          ...fileVersion,
          revision: fileVersion.revision,
        }));

      setRevisions(allFileVersionRevisions.sort((a: RevisionRow, b: RevisionRow) => b.createdDate! - a.createdDate!));
    }
  }, [fileVersions]);

  const userAvatar = (userId: string) => <UserNameTagComponent id={userId} />;

  return (
    <div className="p-6 rounded-lg shadow-sm">
      {
        <div className="space-y-4">
          {revisions.map((revision: RevisionRow, index: number) => (
            <div key={index} className="mb-4 last:mb-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold">{(revision.revision as RevisionInfo)!.value}</span>
                <span className="text-bold">V{revision.fileVersionNumber}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span>{t("Created by")}</span>
                <div className="flex items-center">{userAvatar((revision.revision as RevisionInfo)!.createdBy!)}</div>
                <span className="text-sm">
                  {convertDate(Number((revision.revision as RevisionInfo)!.createdDate), "dateForText")}
                </span>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

export default RevisionContent;
